import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { getImageUrl } from './imageHelper';
import { 
  Plus, X, Search, Edit2, Trash2, Save, Eye, Move, Check, AlertCircle, Upload, Download, CheckSquare, Square
} from 'lucide-react';

// Variant Family Modal
const VariantFamilyModal = ({ isOpen, onClose, onSave, family = null, allProducts }) => {
  const [familyName, setFamilyName] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [defaultProductId, setDefaultProductId] = useState('');
  const [variantLabels, setVariantLabels] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Image state
  const [familyImage, setFamilyImage] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // ✅ FIX: Store fetched products for the family being edited
  const [familyProducts, setFamilyProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  // ✅ NEW: Search results from database
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // ✅ FIX: Reset state and fetch products when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('🔄 Modal opened, family:', family?.family_name);
      setFamilyName(family?.family_name || '');
      setSelectedProducts(family?.product_ids || []);
      setDefaultProductId(family?.default_product_id || '');
      setVariantLabels(family?.variant_labels || {});
      setFamilyImage(family?.image || '');
      setSearchQuery('');
      setFamilyProducts([]);
      setSearchResults([]);
      
      // ✅ FIX: Always fetch family products when editing
      if (family?.product_ids?.length > 0) {
        fetchFamilyProducts(family.product_ids);
      }
    }
  }, [family, isOpen]);

  // ✅ NEW: Search products from database when query changes
  useEffect(() => {
    const searchProducts = async () => {
      if (!searchQuery || searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }
      
      setSearching(true);
      try {
        const { data, error } = await supabase
          .from('products')
          .select('id, name, stock_code, price, image')
          .or(`name.ilike.%${searchQuery}%,stock_code.ilike.%${searchQuery}%`)
          .limit(50);
        
        if (error) {
          console.error('Search error:', error);
          return;
        }
        
        console.log('🔍 Search results:', data?.length);
        setSearchResults(data || []);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setSearching(false);
      }
    };
    
    const debounce = setTimeout(searchProducts, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `variant-family-${Date.now()}.${fileExt}`;
      const filePath = `variant-families/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('Foodline Product Images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('Foodline Product Images')
        .getPublicUrl(filePath);

      setFamilyImage(publicUrl);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image: ' + error.message);
    } finally {
      setUploadingImage(false);
    }
  };

  // Get image from default product or first product with image
  const getFallbackImage = () => {
    // First try default product
    const defaultProduct = familyProducts.find(p => p.id === defaultProductId) ||
                          allProducts.find(p => p.id === defaultProductId);
    if (defaultProduct?.image) return defaultProduct.image;

    // Then try any product in the family
    for (const productId of selectedProducts) {
      const product = familyProducts.find(p => p.id === productId) ||
                     allProducts.find(p => p.id === productId);
      if (product?.image) return product.image;
    }
    return '';
  };

  // ✅ FIX: Fetch ALL products in the family directly from database
  const fetchFamilyProducts = async (productIds) => {
    console.log('📦 Fetching family products:', productIds);
    setLoadingProducts(true);
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .in('id', productIds);
      
      if (error) {
        console.error('❌ Error fetching family products:', error);
        return;
      }
      
      console.log('✅ Fetched family products:', data?.map(p => p.name));
      setFamilyProducts(data || []);
    } catch (err) {
      console.error('❌ Error:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  // ✅ FIX: Combine allProducts with familyProducts (deduped)
  const allProductIds = new Set(allProducts.map(p => p.id));
  const uniqueFamilyProducts = familyProducts.filter(p => !allProductIds.has(p.id));
  const combinedProducts = [...allProducts, ...uniqueFamilyProducts];
  
  // ✅ FIX: For selected products list, prefer familyProducts (freshly fetched)
  const selectedProductsList = selectedProducts.map(id => {
    // First try familyProducts (freshly fetched), then fall back to allProducts
    return familyProducts.find(p => p.id === id) || 
           allProducts.find(p => p.id === id) ||
           searchResults.find(p => p.id === id);
  }).filter(Boolean); // Remove any undefined
  
  // ✅ NEW: Use database search results when searching, otherwise show allProducts
  const filteredProducts = searchQuery.length >= 2
    ? searchResults.filter(p => !selectedProducts.includes(p.id))
    : combinedProducts.filter(p => 
        !selectedProducts.includes(p.id)
      ).slice(0, 20); // Limit to 20 when not searching

  const toggleProduct = (productId) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
      const newLabels = { ...variantLabels };
      delete newLabels[productId];
      setVariantLabels(newLabels);
      if (defaultProductId === productId) {
        setDefaultProductId('');
      }
    } else {
      setSelectedProducts(prev => [...prev, productId]);
      if (selectedProducts.length === 0) {
        setDefaultProductId(productId);
      }
      // ✅ NEW: Add the product to familyProducts if it came from search
      const searchProduct = searchResults.find(p => p.id === productId);
      if (searchProduct && !familyProducts.find(p => p.id === productId)) {
        setFamilyProducts(prev => [...prev, searchProduct]);
      }
    }
  };

  const updateVariantLabel = (productId, label) => {
    setVariantLabels(prev => ({ ...prev, [productId]: label }));
  };

  const handleSave = async () => {
    if (!familyName || selectedProducts.length < 2) {
      alert('Please enter a family name and select at least 2 products');
      return;
    }
    if (!defaultProductId) {
      alert('Please select a default variant');
      return;
    }

    // ✅ FIX: Only generate new key for NEW families, keep existing key for edits
    const familyKey = family?.family_key 
      ? family.family_key 
      : familyName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    
    // Use uploaded image or fallback to product image
    const imageToSave = familyImage || getFallbackImage();
    
    setSaving(true);
    try {
      const familyData = {
        family_name: familyName,
        family_key: familyKey,
        product_ids: selectedProducts,
        default_product_id: defaultProductId,
        variant_labels: variantLabels,
        image: imageToSave
      };

      if (family) {
        // Update existing
        const { error } = await supabase
          .from('product_variant_config')
          .update({
            ...familyData,
            updated_at: new Date().toISOString()
          })
          .eq('id', family.id);

        if (error) throw error;
      } else {
        // Create new
        const { error } = await supabase
          .from('product_variant_config')
          .insert(familyData);

        if (error) throw error;
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving variant family:', error);
      
      // ✅ FIX: User-friendly error messages
      if (error.message?.includes('product_variant_config_family_key_key') || 
          error.code === '23505') {
        alert(`❌ A variant family with the name "${familyName}" already exists.\n\nPlease choose a different name.`);
      } else if (error.message?.includes('duplicate key')) {
        alert(`❌ This variant family already exists.\n\nPlease choose a different name.`);
      } else {
        alert('Failed to save variant family: ' + error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {family ? 'Edit Variant Family' : 'Create Variant Family'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Family Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Family Name *
            </label>
            <input
              type="text"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              placeholder="e.g., Lays Chips, Coca Cola"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              This name will be shown to customers
            </p>
          </div>

          {/* Family Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Family Image
            </label>
            <div className="flex items-start gap-4">
              {/* Image Preview */}
              <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                {(familyImage || getFallbackImage()) ? (
                  <img 
                    src={familyImage || getFallbackImage()} 
                    alt="Family" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <Upload size={24} />
                  </div>
                )}
              </div>
              
              {/* Upload Controls */}
              <div className="flex-1">
                <label className="cursor-pointer">
                  <div className={`inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <Upload size={18} />
                    <span className="text-sm font-medium text-gray-700">
                      {uploadingImage ? 'Uploading...' : 'Upload Image'}
                    </span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    className="hidden"
                  />
                </label>
                
                {familyImage && (
                  <button
                    onClick={() => setFamilyImage('')}
                    className="ml-2 text-sm text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                )}
                
                <p className="text-xs text-gray-500 mt-2">
                  {familyImage 
                    ? 'Custom image uploaded' 
                    : selectedProducts.length > 0 
                      ? 'No custom image - will use product image' 
                      : 'Upload an image or select products to use their image'}
                </p>
              </div>
            </div>
          </div>

          {/* Selected Products */}
          {selectedProducts.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Selected Products ({selectedProducts.length})
              </h3>
              
              {/* ✅ FIX: Loading indicator */}
              {loadingProducts && (
                <div className="mb-3 p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
                  <p className="text-sm text-gray-600">Loading products...</p>
                </div>
              )}
              
              {/* ✅ FIX: Warning for missing products (only show after loading) */}
              {!loadingProducts && selectedProducts.length > selectedProductsList.length && (
                <div className="mb-3 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-2">
                  <AlertCircle size={18} className="text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-orange-800">
                      {selectedProducts.length - selectedProductsList.length} product(s) not found
                    </p>
                    <p className="text-xs text-orange-600">
                      These products may have been deleted from the database.
                    </p>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                {selectedProductsList.map(product => (
                  <div key={product.id} className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <img src={getImageUrl(product.image)} alt={product.name} className="w-12 h-12 object-cover rounded" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                      <p className="text-xs text-gray-500">Stock: {product.stock_code}</p>
                      <input
                        type="text"
                        value={variantLabels[product.id] || ''}
                        onChange={(e) => updateVariantLabel(product.id, e.target.value)}
                        placeholder="Variant label (e.g., Original, BBQ)"
                        className="w-full mt-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-green-500"
                      />
                    </div>
                    <button
                      onClick={() => setDefaultProductId(product.id)}
                      className={`px-3 py-1 rounded text-xs font-semibold transition-colors whitespace-nowrap ${
                        defaultProductId === product.id
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {defaultProductId === product.id ? '✓ Default' : 'Set Default'}
                    </button>
                    <button
                      onClick={() => toggleProduct(product.id)}
                      className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add Products */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Add Products to Family
            </h3>
            <div className="relative mb-3">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or stock code..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
              {searching ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-green-600 border-t-transparent mx-auto mb-2"></div>
                  <p className="text-gray-500 text-sm">Searching...</p>
                </div>
              ) : filteredProducts.length === 0 ? (
                <p className="text-center py-8 text-gray-500">
                  {searchQuery.length < 2 
                    ? 'Type at least 2 characters to search products' 
                    : searchQuery 
                      ? 'No products found' 
                      : 'All products selected'}
                </p>
              ) : (
                filteredProducts.map(product => (
                  <button
                    key={product.id}
                    onClick={() => toggleProduct(product.id)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 border-b border-gray-100 last:border-0 text-left"
                  >
                    <img src={getImageUrl(product.image)} alt="" className="w-12 h-12 object-cover rounded" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                      <p className="text-xs text-gray-500">
                        Stock: {product.stock_code} • R{product.price.toFixed(2)}
                      </p>
                    </div>
                    <Plus size={18} className="text-green-600" />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || selectedProducts.length < 2 || !familyName}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save size={18} />
            {saving ? 'Saving...' : (family ? 'Update Family' : 'Create Family')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Preview Modal
const PreviewModal = ({ isOpen, onClose, family, allProducts }) => {
  if (!isOpen || !family) return null;

  const familyProducts = allProducts.filter(p => family.product_ids.includes(p.id));
  const defaultProduct = allProducts.find(p => p.id === family.default_product_id);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Preview: {family.family_name}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          <div className="mb-6">
            <img
              src={getImageUrl(family.image || defaultProduct?.image)}
              alt={family.family_name}
              className="w-full h-64 object-contain rounded-lg bg-gray-50"
            />
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-4">{family.family_name}</h3>
          
          <div className="space-y-3">
            <p className="text-sm font-semibold text-gray-700 uppercase">Select Variant:</p>
            <div className="grid grid-cols-2 gap-3">
              {familyProducts.map(product => {
                const isDefault = product.id === family.default_product_id;
                return (
                  <div
                    key={product.id}
                    className={`p-3 rounded-lg border-2 cursor-pointer ${
                      isDefault ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <img src={getImageUrl(product.image)} alt="" className="w-16 h-16 object-cover rounded" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {family.variant_labels?.[product.id] || product.name}
                        </p>
                        <p className="text-lg font-bold text-green-600">R{product.price.toFixed(2)}</p>
                      </div>
                    </div>
                    {isDefault && (
                      <span className="inline-block mt-2 text-xs bg-green-600 text-white px-2 py-1 rounded">
                        Default Selection
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Variants Screen
const VariantsScreen = ({ products, onRefresh }) => {
  const [variantFamilies, setVariantFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingFamily, setEditingFamily] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewFamily, setPreviewFamily] = useState(null);
  
  // Selection and bulk action states
  const [selectedFamilies, setSelectedFamilies] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchVariantFamilies();
  }, []);

  const fetchVariantFamilies = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('product_variant_config')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVariantFamilies(data || []);
    } catch (error) {
      console.error('Error fetching variant families:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFamily = () => {
    fetchVariantFamilies();
  };

  const handleEditFamily = (family) => {
    setEditingFamily(family);
    setShowModal(true);
  };

  const handleDeleteFamily = async (familyId) => {
    if (!window.confirm('Are you sure you want to delete this variant family? The products will remain in the database.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('product_variant_config')
        .delete()
        .eq('id', familyId);

      if (error) throw error;
      fetchVariantFamilies();
    } catch (error) {
      console.error('Error deleting variant family:', error);
      alert('Failed to delete variant family: ' + error.message);
    }
  };

  const handlePreview = (family) => {
    setPreviewFamily(family);
    setShowPreview(true);
  };

  const getProductInfo = (productId) => {
    return products.find(p => p.id === productId);
  };

  // Selection handlers
  const handleSelectAll = () => {
    if (selectedFamilies.size === variantFamilies.length) {
      setSelectedFamilies(new Set());
    } else {
      setSelectedFamilies(new Set(variantFamilies.map(f => f.id)));
    }
  };

  const handleToggleFamily = (familyId) => {
    const newSelected = new Set(selectedFamilies);
    if (newSelected.has(familyId)) {
      newSelected.delete(familyId);
    } else {
      newSelected.add(familyId);
    }
    setSelectedFamilies(newSelected);
  };

  // Bulk publish variant families WITH DEBUG LOGGING
  const handleBulkPublish = async () => {
    if (selectedFamilies.size === 0) {
      alert('Please select variant families to publish');
      return;
    }

    if (!window.confirm(`Publish ${selectedFamilies.size} selected variant family(ies)? This will publish all products in these families.`)) {
      return;
    }

    setBulkActionLoading(true);
    try {
      console.log('🔄 Starting bulk publish...');
      console.log('📋 Selected families:', Array.from(selectedFamilies));
      
      const selectedFamilyObjects = variantFamilies.filter(f => selectedFamilies.has(f.id));
      console.log('📦 Family objects:', selectedFamilyObjects);
      
      // Collect all unique product IDs from selected families
      const allProductIds = new Set();
      selectedFamilyObjects.forEach(family => {
        console.log(`📋 Family "${family.family_name}" has ${family.product_ids.length} products:`, family.product_ids);
        family.product_ids.forEach(id => allProductIds.add(id));
      });

      const productIds = Array.from(allProductIds);
      console.log(`✅ Total unique products to publish: ${productIds.length}`);
      console.log('🆔 Product IDs:', productIds);
      
      // Update published field on all products in batches
      const batchSize = 100;
      let successCount = 0;
      
      for (let i = 0; i < productIds.length; i += batchSize) {
        const batch = productIds.slice(i, i + batchSize);
        console.log(`📤 Publishing batch ${Math.floor(i / batchSize) + 1} (${batch.length} products):`, batch);
        
        const { data, error } = await supabase
          .from('products')
          .update({ published: true })
          .in('id', batch)
          .select('id, name, published'); // Select to see what was updated

        if (error) {
          console.error('❌ Error in batch:', error);
          throw error;
        }
        
        console.log('✅ Batch update successful! Updated products:', data);
        successCount += batch.length;
      }

      console.log(`✅ Successfully updated ${successCount} products to published = true`);

      // Update the variant families to mark them as published
      console.log('📤 Updating variant family configs...');
      const { data: familyData, error: familyError } = await supabase
        .from('product_variant_config')
        .update({ 
          published: true,
          updated_at: new Date().toISOString()
        })
        .in('id', Array.from(selectedFamilies))
        .select(); // Select to see what was updated

      if (familyError) {
        console.error('❌ Error updating families:', familyError);
        throw familyError;
      }
      
      console.log('✅ Family configs updated:', familyData);
      console.log('🎉 Bulk publish completed successfully!');

      alert(`✅ Successfully published ${selectedFamilies.size} variant family(ies) with ${successCount} product(s)!`);
      setSelectedFamilies(new Set());
      fetchVariantFamilies();
      onRefresh();
    } catch (error) {
      console.error('❌ FATAL ERROR in bulk publishing:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      alert('Failed to publish variant families: ' + error.message);
    } finally {
      setBulkActionLoading(false);
      console.log('🏁 Bulk publish operation finished');
    }
  };

  // Bulk unpublish variant families
  const handleBulkUnpublish = async () => {
    if (selectedFamilies.size === 0) {
      alert('Please select variant families to unpublish');
      return;
    }

    if (!window.confirm(`Unpublish ${selectedFamilies.size} selected variant family(ies)? This will unpublish all products in these families.`)) {
      return;
    }

    setBulkActionLoading(true);
    try {
      console.log('🔄 Starting bulk unpublish...');
      
      const selectedFamilyObjects = variantFamilies.filter(f => selectedFamilies.has(f.id));
      
      // Collect all unique product IDs from selected families
      const allProductIds = new Set();
      selectedFamilyObjects.forEach(family => {
        family.product_ids.forEach(id => allProductIds.add(id));
      });

      const productIds = Array.from(allProductIds);
      console.log(`Unpublishing ${productIds.length} products`);
      
      // Update published field on all products in batches
      const batchSize = 100;
      let successCount = 0;
      
      for (let i = 0; i < productIds.length; i += batchSize) {
        const batch = productIds.slice(i, i + batchSize);
        
        const { error } = await supabase
          .from('products')
          .update({ published: false })
          .in('id', batch);

        if (error) {
          console.error(`Error in batch ${i / batchSize + 1}:`, error);
          throw error;
        }
        
        successCount += batch.length;
      }

      // Update the variant families to mark them as unpublished
      const { error: familyError } = await supabase
        .from('product_variant_config')
        .update({ 
          published: false,
          updated_at: new Date().toISOString()
        })
        .in('id', Array.from(selectedFamilies));

      if (familyError) throw familyError;

      console.log('✅ Unpublish completed');
      alert(`✅ Successfully unpublished ${selectedFamilies.size} variant family(ies) with ${successCount} product(s)!`);
      setSelectedFamilies(new Set());
      fetchVariantFamilies();
      onRefresh();
    } catch (error) {
      console.error('Error bulk unpublishing variant families:', error);
      alert('Failed to unpublish variant families: ' + error.message);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Calculate stats
  const productsInFamilies = new Set();
  variantFamilies.forEach(family => {
    family.product_ids.forEach(id => productsInFamilies.add(id));
  });

  const publishedFamilies = variantFamilies.filter(f => f.published !== false).length;
  const unpublishedFamilies = variantFamilies.length - publishedFamilies;

  const stats = {
    totalFamilies: variantFamilies.length,
    publishedFamilies,
    unpublishedFamilies,
    productsInFamilies: productsInFamilies.size,
    standaloneProducts: products.length - productsInFamilies.size
  };

  // Filter variant families based on search query
  const filteredVariantFamilies = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return variantFamilies;
    }
    
    const query = searchQuery.toLowerCase();
    return variantFamilies.filter(family => 
      family.family_name.toLowerCase().includes(query) ||
      family.family_key.toLowerCase().includes(query)
    );
  }, [variantFamilies, searchQuery]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Product Variants</h1>
          <p className="text-gray-600">
            Group products into variant families without modifying the products table
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-green-600 text-white font-bold px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
        >
          <Plus size={20} />
          Create Variant Family
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Total Families</p>
          <p className="text-3xl font-bold text-gray-900">{stats.totalFamilies}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Published</p>
          <p className="text-3xl font-bold text-green-600">{stats.publishedFamilies}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Unpublished</p>
          <p className="text-3xl font-bold text-orange-600">{stats.unpublishedFamilies}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Products in Families</p>
          <p className="text-3xl font-bold text-gray-900">{stats.productsInFamilies}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Standalone Products</p>
          <p className="text-3xl font-bold text-gray-900">{stats.standaloneProducts}</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search variant families by name or key..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-10 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Search Results Count */}
      {searchQuery && (
        <div className="text-sm text-gray-600">
          Found <span className="font-semibold">{filteredVariantFamilies.length}</span> variant {filteredVariantFamilies.length === 1 ? 'family' : 'families'}
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedFamilies.size > 0 && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckSquare size={24} className="text-blue-600" />
              <span className="font-semibold text-gray-900">
                {selectedFamilies.size} variant family(ies) selected
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleBulkPublish}
                disabled={bulkActionLoading}
                className="px-4 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Upload size={18} />
                Publish Selected
              </button>
              <button
                onClick={handleBulkUnpublish}
                disabled={bulkActionLoading}
                className="px-4 py-2 bg-orange-600 text-white font-bold rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Download size={18} />
                Unpublish Selected
              </button>
              <button
                onClick={() => setSelectedFamilies(new Set())}
                className="px-4 py-2 bg-gray-500 text-white font-bold rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2"
              >
                <X size={18} />
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Variant Families List */}
      <div className="space-y-4">
        {filteredVariantFamilies.length === 0 && !searchQuery ? (
          <div className="bg-white rounded-xl p-12 text-center border-2 border-dashed border-gray-300">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Move size={32} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No variant families yet</h3>
            <p className="text-gray-600 mb-6">Create your first variant family to group related products</p>
            <button
              onClick={() => setShowModal(true)}
              className="bg-green-600 text-white font-bold px-6 py-3 rounded-lg hover:bg-green-700"
            >
              Create Variant Family
            </button>
          </div>
        ) : filteredVariantFamilies.length === 0 && searchQuery ? (
          <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
            <Search className="mx-auto text-gray-300 mb-3" size={48} />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No variant families found</h3>
            <p className="text-gray-600 mb-6">Try a different search term</p>
            <button
              onClick={() => setSearchQuery('')}
              className="text-green-600 font-semibold hover:text-green-700"
            >
              Clear search
            </button>
          </div>
        ) : (
          <>
            {/* Select All Header */}
            {filteredVariantFamilies.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <button
                  onClick={handleSelectAll}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  {selectedFamilies.size === variantFamilies.length ? (
                    <CheckSquare size={20} className="text-green-600" />
                  ) : (
                    <Square size={20} />
                  )}
                  Select All Variant Families
                </button>
              </div>
            )}
            
            {filteredVariantFamilies.map(family => {
              const defaultProduct = getProductInfo(family.default_product_id);
              const isPublished = family.published !== false;
              const isSelected = selectedFamilies.has(family.id);
              
              return (
                <div 
                  key={family.id} 
                  className={`bg-white rounded-xl border overflow-hidden ${
                    isPublished ? 'border-gray-200' : 'border-orange-300 bg-orange-50/30'
                  }`}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-4 flex-1">
                        {/* Selection checkbox */}
                        <button
                          onClick={() => handleToggleFamily(family.id)}
                          className="flex-shrink-0"
                        >
                          {isSelected ? (
                            <CheckSquare size={24} className="text-green-600" />
                          ) : (
                            <Square size={24} className="text-gray-400 hover:text-gray-600" />
                          )}
                        </button>
                        
                        <img 
                          src={getImageUrl(family.image || defaultProduct?.image)} 
                          alt={family.family_name}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-xl font-bold text-gray-900">{family.family_name}</h3>
                            {!isPublished && (
                              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded font-semibold">
                                Unpublished
                              </span>
                            )}
                            {isPublished && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-semibold">
                                Published
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            {family.product_ids.length} variants • Key: <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">{family.family_key}</code>
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handlePreview(family)}
                          className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
                          title="Preview"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => handleEditFamily(family)}
                          className="p-2 hover:bg-green-100 rounded-lg text-green-600"
                          title="Edit"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteFamily(family.id)}
                          className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>

                    {/* Variant List */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {family.product_ids.map(productId => {
                        const product = getProductInfo(productId);
                        if (!product) return null;
                        const isDefault = productId === family.default_product_id;
                        return (
                          <div 
                            key={productId}
                            className={`flex items-center gap-3 p-3 rounded-lg border ${
                              isDefault ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50'
                            }`}
                          >
                            <img src={getImageUrl(product.image)} alt="" className="w-12 h-12 object-cover rounded" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {family.variant_labels?.[productId] || product.name}
                              </p>
                              <p className="text-xs text-gray-600">R{product.price.toFixed(2)} • {product.stock_code}</p>
                            </div>
                            {isDefault && (
                              <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full font-semibold">
                                Default
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Modals */}
      <VariantFamilyModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingFamily(null);
        }}
        onSave={handleSaveFamily}
        family={editingFamily}
        allProducts={products}
      />

      <PreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        family={previewFamily}
        allProducts={products}
      />
    </div>
  );
};

export default VariantsScreen;
